import { Add } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
    PARTNER_SERVICE_TYPES,
    PartnerServiceType,
    useOwnerContractorsByType,
} from '../../Owner/contractorSelectionUtils';
import ContractorSelectionCard from './ContractorSelectionCard';
import AutoHeightTransition from '../../../../../shared/AutoHeightTransition';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { Close } from '@mui/icons-material';

interface AddPartnerContractorDialogProps {
    open: boolean;
    selectedIds: number[];
    setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
    onClose: () => void;
    onConfirm: (selectedIds: number[]) => void;
    serviceType: PartnerServiceType;
    setServiceType: React.Dispatch<React.SetStateAction<PartnerServiceType>>;
}

const AddPartnerContractorDialog = ({
    open,
    selectedIds,
    setSelectedIds,
    onClose,
    onConfirm,
    serviceType,
    setServiceType,
}: AddPartnerContractorDialogProps) => {
    const contractors = useOwnerContractorsByType(serviceType);

    const handleServiceTypeChange = (
        _: React.MouseEvent<HTMLElement>,
        value: PartnerServiceType | null,
    ) => {
        if (!value) {
            return;
        }

        setServiceType(value);
    };

    const handleToggle = (id: number) => {
        setSelectedIds((current) =>
            current.includes(id)
                ? current.filter((item) => item !== id)
                : [...current, id],
        );
    };

    const handleConfirm = () => {
        onConfirm(selectedIds);
        onClose();
    };

    const handleAddContractor = () => {
        onClose();
        window.open(
            '/contractors?openCreateContractor=true',
            '_blank',
            'noopener,noreferrer',
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
            <DialogTitle>
                Add partner contractors{' '}
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'text.primary' }}
                    aria-label='close'
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <AutoHeightTransition>
                    <Stack spacing={2}>
                        <ToggleButtonGroup
                            exclusive
                            value={serviceType}
                            onChange={handleServiceTypeChange}
                            aria-label='Partner service type'
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {PARTNER_SERVICE_TYPES.map((type) => (
                                <ToggleButton key={type} value={type}>
                                    {camelCaseToTitleCase(type)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        {contractors.length === 0 ? (
                            <Stack spacing={1.5} alignItems='flex-start'>
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    No {camelCaseToTitleCase(serviceType)}{' '}
                                    contractors are available for your account.
                                </Typography>
                                <Button
                                    startIcon={<Add />}
                                    onClick={handleAddContractor}
                                >
                                    Invite a {camelCaseToTitleCase(serviceType)}{' '}
                                    contractor
                                </Button>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                {contractors.map((contractor) => (
                                    <Stack key={contractor.id} spacing={0.75}>
                                        <ContractorSelectionCard
                                            contractor={contractor}
                                            mode='picker'
                                            selected={selectedIds.includes(
                                                contractor.id,
                                            )}
                                            onToggle={() =>
                                                handleToggle(contractor.id)
                                            }
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </AutoHeightTransition>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant='contained' onClick={handleConfirm}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPartnerContractorDialog;
